import { z } from "zod";

import {
  DOMAINS,
  type AttemptResult,
  type ChildProfile,
  type Difficulty,
  type DomainMastery,
  type RewardState
} from "@/domain";

export const MAX_CHILD_PROFILES = 3;
export const PROFILE_EXPORT_SCHEMA_VERSION = 1 as const;

const difficultySchema: z.ZodType<Difficulty> = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6)
]);

const isoDateTimeSchema = z.iso.datetime({ offset: true });

export const childProfileSchema = z.strictObject({
  id: z.string().min(1),
  nickname: z.string().min(1),
  avatarId: z.string().min(1),
  createdAt: isoDateTimeSchema,
  parentPinHash: z.string().min(1).optional()
}) satisfies z.ZodType<ChildProfile>;

export type PortableChildProfile = Omit<ChildProfile, "parentPinHash">;

const portableChildProfileSchema = childProfileSchema.transform(
  ({ id, nickname, avatarId, createdAt }): PortableChildProfile => ({
    id,
    nickname,
    avatarId,
    createdAt
  })
);

export const attemptResultSchema = z.strictObject({
  id: z.string().min(1),
  profileId: z.string().min(1),
  levelId: z.string().min(1),
  domain: z.enum(DOMAINS),
  difficulty: difficultySchema,
  correctRate: z.number().finite().min(0).max(1),
  hintCount: z.number().int().nonnegative(),
  retryCount: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
  completedAt: isoDateTimeSchema
}) satisfies z.ZodType<AttemptResult>;

export const domainMasterySchema = z.strictObject({
  domain: z.enum(DOMAINS),
  score: z.number().finite(),
  recommendedDifficulty: difficultySchema,
  updatedAt: isoDateTimeSchema
}) satisfies z.ZodType<DomainMastery>;

export const rewardStateSchema = z.strictObject({
  stamps: z.number().int().nonnegative(),
  unlockedDecorationIds: z.array(z.string().min(1))
}) satisfies z.ZodType<RewardState>;

export interface ProfileExportData {
  schemaVersion: typeof PROFILE_EXPORT_SCHEMA_VERSION;
  profile: PortableChildProfile;
  attempts: AttemptResult[];
  mastery: DomainMastery[];
  rewards: RewardState;
}

interface ProfileExportInput extends Omit<ProfileExportData, "profile"> {
  profile: ChildProfile;
}

const profileExportSchema = z
  .strictObject({
    schemaVersion: z.literal(PROFILE_EXPORT_SCHEMA_VERSION),
    profile: portableChildProfileSchema,
    attempts: z.array(attemptResultSchema),
    mastery: z.array(domainMasterySchema),
    rewards: rewardStateSchema
  })
  .superRefine((data, context) => {
    const attemptIds = new Set<string>();

    data.attempts.forEach((attempt, index) => {
      if (attempt.profileId !== data.profile.id) {
        context.addIssue({
          code: "custom",
          message: "Attempt belongs to a different profile.",
          path: ["attempts", index, "profileId"]
        });
      }

      if (attemptIds.has(attempt.id)) {
        context.addIssue({
          code: "custom",
          message: "Attempt identifiers must be unique within a profile.",
          path: ["attempts", index, "id"]
        });
      }
      attemptIds.add(attempt.id);
    });

    const masteryDomains = new Set<string>();
    data.mastery.forEach((mastery, index) => {
      if (masteryDomains.has(mastery.domain)) {
        context.addIssue({
          code: "custom",
          message: "Mastery domains must be unique within a profile.",
          path: ["mastery", index, "domain"]
        });
      }
      masteryDomains.add(mastery.domain);
    });
  });

export class ProfileNotFoundError extends Error {
  override readonly name = "ProfileNotFoundError";

  constructor(profileId: string) {
    super('Profile "' + profileId + '" was not found.');
  }
}

export class ProfileLimitError extends Error {
  override readonly name = "ProfileLimitError";

  constructor() {
    super("Only " + MAX_CHILD_PROFILES + " child profiles can be stored.");
  }
}

export class InvalidProfileImportError extends Error {
  override readonly name = "InvalidProfileImportError";

  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
  }
}

export function parseChildProfile(profile: unknown): ChildProfile {
  return childProfileSchema.parse(profile);
}

export function parseAttemptResult(attempt: unknown): AttemptResult {
  return attemptResultSchema.parse(attempt);
}

export function parseMastery(mastery: unknown): DomainMastery[] {
  const result = z.array(domainMasterySchema).parse(mastery);
  const domains = new Set(result.map((entry) => entry.domain));

  if (domains.size !== result.length) {
    throw new Error("Mastery domains must be unique within a profile.");
  }

  return result;
}

export function parseRewardState(rewards: unknown): RewardState {
  return rewardStateSchema.parse(rewards);
}

export function decodeProfileExport(serialized: string): ProfileExportData {
  let value: unknown;

  try {
    value = JSON.parse(serialized) as unknown;
  } catch (error) {
    throw new InvalidProfileImportError("Profile import is not valid JSON.", {
      cause: error
    });
  }

  const result = profileExportSchema.safeParse(value);
  if (!result.success) {
    throw new InvalidProfileImportError(
      "Profile import does not match the supported schema.",
      { cause: result.error }
    );
  }

  return result.data;
}

export function encodeProfileExport(data: ProfileExportInput): string {
  return JSON.stringify(profileExportSchema.parse(data));
}

export function createEmptyRewards(): RewardState {
  return { stamps: 0, unlockedDecorationIds: [] };
}

export function sortProfiles(profiles: ChildProfile[]): ChildProfile[] {
  return profiles.sort(
    (left, right) =>
      left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id)
  );
}

export function sortAttempts(attempts: AttemptResult[]): AttemptResult[] {
  return attempts.sort(
    (left, right) =>
      left.completedAt.localeCompare(right.completedAt) || left.id.localeCompare(right.id)
  );
}

export function sortMastery(mastery: DomainMastery[]): DomainMastery[] {
  const domainOrder = new Map(DOMAINS.map((domain, index) => [domain, index]));
  return mastery.sort(
    (left, right) =>
      (domainOrder.get(left.domain) ?? 0) - (domainOrder.get(right.domain) ?? 0)
  );
}
